import { useMemo } from "react";
import { computeNextPaymentDate, type Frequency } from "@/lib/billingSchedule";
import { type Subscription } from "@/types/subscription";

export const useDerivedNextPayment = (
  subscription: Subscription,
  repoPayments: any[],
) => {
  return useMemo(() => {
    // PAYG: only unscheduled (no frequency) returns null. Scheduled PAYG derives next date.
    const isPAYG = (subscription as any)?.billingType === "pay_as_you_go";
    const freq = subscription.frequency;
    if (isPAYG && !freq) {
      return null;
    }

    // Consider only successful payments
    const paidDates: Date[] = [
      ...(subscription.paymentHistory || [])
        .filter((p) => p.status === "paid")
        .map((p) => new Date(p.date)),
      ...repoPayments
        .filter((p: any) => p.status === "paid")
        .map((p: any) => new Date(p.payment_date)),
    ];

    // Prefer explicit nextPayment when present, otherwise anchor on startDate
    const seedNext: Date | null =
      (subscription as any).nextPayment ??
      (subscription as any).startDate ??
      null;

    // If no payments and a future explicit nextPayment exists, preserve it
    // to avoid changing time-of-day expected by existing code/tests.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (
      paidDates.length === 0 &&
      seedNext instanceof Date &&
      !isNaN(seedNext.getTime()) &&
      seedNext.getTime() > today.getTime()
    ) {
      return new Date(seedNext);
    }

    // If no frequency at this point, we cannot derive a next date
    if (!freq) {
      return null;
    }

    return computeNextPaymentDate({
      frequency: freq as Frequency,
      customFrequencyDays: subscription.customFrequencyDays,
      paidDates,
      seedNextPayment: seedNext,
    });
  }, [subscription, repoPayments]);
};
